import {
  switchEnterpriseResponseSchema,
  type SwitchEnterpriseResponse,
  type WorkosLoginResponse,
} from "@mg/schemas/src/prince/auth";

/**
 * The MFA step arm of a `POST /enterprise/switch` response.
 *
 * A per-enterprise MFA tenant answers the switch with the WorkOS challenge
 * instead of a session, so the caller has to prove a TOTP code before the
 * switch happens.
 *
 * why: this arm HAS producers, and since #6156 `switchEnterpriseResponseSchema`
 * declares it. Two routes answer a proof-less switch into a flagged enterprise
 * with this payload as a 200, both minted by `startEnterpriseMfaStep`
 * (`services/christo/app/controllers/prince-auth/handlers/enterpriseMfaStep.ts`):
 * `POST /enterprise/switch`
 * (`services/christo/app/controllers/prince-auth/auth.ts`) and the share-link
 * auto-switch (`services/christo/app/handlers/catalyst/utils.ts`). Do not read
 * this branch as dead code and delete it — that was true before #6156 and the
 * note saying so outlived the change.
 *
 * why: the challenge and factor are both optional in THIS type — they are picked
 * off `WorkosLoginResponse`, where they are — while the schema requires the
 * challenge on its step arm and `isEnterpriseMfaStep` requires
 * `authenticationChallenge.id`. The type tracks the schema's step arm (since
 * #6156 the schema is a `z.union` and `readSwitchEnterpriseResponse` returns
 * that arm's inferred type, so the type must stay assignable from it). The GUARD
 * is still what narrows to a challenge-bearing step: `mfaOptionsOf` feeds
 * `authenticationChallenge.id` into `POST /workos-mfa` as a required body field,
 * so a challenge-less body is not a steppable switch — and since the schema
 * requires the challenge, such a body is refused before the guard is reached.
 *
 * This module is a leaf on purpose. `services/auth.ts` reads it to classify the
 * switch response, and `utils/enterpriseSwitch.ts` reads it to park the step, so
 * it must not reach back into either (that direction is the store-cycle the
 * `switchEnterpriseWithMfa` why-comment records).
 */
export type EnterpriseMfaStep = {
  enterpriseMfaRequired: true;
  enterpriseId: string;
  pendingAuthenticationToken: string;
} & Pick<
  WorkosLoginResponse,
  "authenticationChallenge" | "authenticationFactor"
>;

/**
 * Is this switch response the MFA step arm?
 *
 * Takes `unknown` on purpose: the response is read before any schema claims it,
 * so there is no concrete type to narrow from. `=== true` (not a truthy read)
 * is what keeps a success arm carrying an unrelated `enterpriseMfaRequired`
 * field from being mistaken for a step.
 */
export function isEnterpriseMfaStep(res: unknown): res is EnterpriseMfaStep {
  if (typeof res !== "object" || res === null) return false;
  const step = res as Partial<EnterpriseMfaStep>;
  return (
    step.enterpriseMfaRequired === true &&
    typeof step.enterpriseId === "string" &&
    typeof step.pendingAuthenticationToken === "string" &&
    typeof step.authenticationChallenge?.id === "string"
  );
}

/**
 * Raised by a resource read the share-link auto-switch answered with the step.
 *
 * `getProject` and `getFoldersAndTickets` return a ticket / a folder listing —
 * the step arm is neither, and letting the ticket schema see it reported a
 * `ZodError` on a body that was never a ticket, so the shared link died as a
 * parse failure. The reads park the step (`openEnterpriseMfaSwitch`, exactly
 * what `switchEnterpriseWithMfa` does for the switch route) and raise this, and
 * the error carries the step because the reads' return type cannot.
 *
 * `enterpriseMfaStepParked` is the marker `isEnterpriseMfaStepParkedError`
 * reads. A plain `instanceof` would do under a modern target, but carrie
 * compiles to es5, where `class X extends Error` is downlevelled and the check
 * is not reliable across bundlers — and a missed check here renders the generic
 * error page over a step the user is being asked to complete.
 */
export class EnterpriseMfaStepParkedError extends Error {
  readonly enterpriseMfaStepParked = true;
  readonly step: EnterpriseMfaStep;

  constructor(step: EnterpriseMfaStep) {
    super("This workspace requires a verification step before the link opens.");
    this.name = "EnterpriseMfaStepParkedError";
    this.step = step;
  }
}

export function isEnterpriseMfaStepParkedError(
  error: unknown,
): error is EnterpriseMfaStepParkedError {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { enterpriseMfaStepParked?: unknown }).enterpriseMfaStepParked ===
      true
  );
}

/**
 * The `refetchInterval` a query should use, given the error it is holding.
 *
 * The parked step is control flow, not a failure, and React Query cannot tell
 * the difference: the polling callers (`useTicketView`,
 * `useChatReviewSummaries`, 5s) re-issue the resource GET on every tick, each
 * one throws this sentinel, and each throw re-dispatches
 * `openEnterpriseMfaSwitch` — which re-opens the TOTP dialog the user is
 * answering, every 5 seconds, until the code clears. Retrying cannot succeed
 * either: the same session re-reads the same step.
 *
 * So both halves of "stop retrying this" read the query's own error rather than
 * the store: `queryClient`'s default `retry` predicate for the backoff attempts,
 * and this for the interval. Taking the error as an argument keeps it a pure
 * function, testable without a provider.
 *
 * The parked state clears on completion, the error goes with it, and the next
 * render resumes polling — which is what loads the resource the step gated.
 */
export function refetchIntervalUnlessMfaStepParked(
  error: unknown,
  fallback: number | false,
): number | false {
  return isEnterpriseMfaStepParkedError(error) ? false : fallback;
}

/**
 * The WorkOS options `AuthenticationDialog` needs to render the TOTP step.
 * `enterpriseMfaRequired` and `enterpriseId` are dropped here: the dialog
 * verifies the code against the pending token, which already names the
 * enterprise the switch is completing.
 */
export function mfaOptionsOf(step: EnterpriseMfaStep): WorkosLoginResponse {
  return {
    pendingAuthenticationToken: step.pendingAuthenticationToken,
    authenticationChallenge: step.authenticationChallenge,
    authenticationFactor: step.authenticationFactor,
  };
}

/**
 * Classify a switch response into the arm the caller has to handle.
 *
 * why: the schema is validated BEFORE the step arm is recognised, because
 * `switchEnterprise` used to parse first — a step-shaped payload then threw a
 * `ZodError` inside the service, so no caller could ever see the step and the
 * switch died as a validation error instead of opening the TOTP step. A
 * response is a step only when it carries the literal discriminator plus a
 * string `enterpriseId`, a string `pendingAuthenticationToken` and a string
 * `authenticationChallenge.id` — the three fields the TOTP step is verified
 * against; every other body still throws the schema's own error.
 *
 * The step arm is read by shape rather than by the schema's type so this
 * compiles both before and after the schema carries the arm: the schema's
 * success branch takes it once the arm is declared, and the guard takes it
 * until then.
 */
export function readSwitchEnterpriseResponse(
  json: unknown,
): SwitchEnterpriseResponse | EnterpriseMfaStep {
  const parsed = switchEnterpriseResponseSchema.safeParse(json);
  if (parsed.success) return parsed.data;
  if (isEnterpriseMfaStep(json)) return json;
  throw parsed.error;
}
